import logo from '@cannect/assets/svg/logo2.svg'
import { Icons, Link, Navigation } from '@cannect/new-components/atoms'
import { useState } from 'react'
import { LuFileSpreadsheet, LuHeart } from 'react-icons/lu'
import { useLocation } from 'react-router-dom'
import { AccountMenuItem } from './AccountMenuItem'
import { HeaderPill } from './HeaderPill'
import SearchAutocomplete from './SearchAutocomplete'
import { useHeader } from './useHeader'

import CartSheet from '@cannect/new-components/CartSheet'
import { CounterBadge } from '@cannect/new-components/molecules'
import { DigitalJourney } from '..'

/**
 * Ajustes do campo de busca para as medidas do header (199x25, texto 12px, ícone 16px).
 * O padding vai via `[&>div]` porque Form.Input repassa `className` ao wrapper E ao container
 * interno — aplicado direto, o px entrava duas vezes e encolhia o campo em 24px.
 */
const SEARCH_INPUT_CLASSES =
  'h-[25px] [&>div]:h-[25px] [&>div]:gap-2 [&>div]:px-3 [&>div]:py-0 [&_input]:pr-0 [&_input]:text-xs [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0'

const ICON_BUTTON_CLASSES =
  'relative flex items-center justify-center rounded-full border-none bg-transparent p-0 text-marketplace-800 transition-colors hover:text-marketplace-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2'

export const HeaderDesktop = ({
  menuConfig,
  headerCtas,
  signedUserName,
  handleLogout,
  isLogged,
  cartProductsQuantity,
  isAssistantFlow,
  isDigitalJourneyFlow,
  handleAssistantContinue,
  isLoadingCartQuantity,
  isPublicFlow,
  showSubscriptions
}: ReturnType<typeof useHeader>) => {
  const [isCartSheetOpen, setIsCartSheetOpen] = useState(false)
  const { pathname } = useLocation()

  const cartIconContent = (
    <>
      <Icons.ShoppingBag className="h-4 w-4" />
      <CounterBadge quantity={cartProductsQuantity} isLoading={isLoadingCartQuantity} />
      <span className="sr-only">Carrinho</span>
    </>
  )

  const isActive = (path?: string) => {
    if (!path || path === '#') return false
    const base = path.split('?')[0].replace(/\/\d+$/, '')
    return base === '/' ? pathname === '/' : pathname.startsWith(base)
  }

  return (
    <>
      {/* Medidas do design: conteúdo de 1166px com margem de 137px em 1440 (max-w 1230 + px-8).
          O `container` do projeto rende 1132px de conteúdo e fazia o campo de busca encolher,
          cortando o "?" do placeholder. */}
      <div className="mx-auto flex w-full max-w-[1230px] items-center justify-between gap-4 bg-gray-50 px-8 py-6">
        {!isPublicFlow && (
          <div className="flex min-w-0 items-center gap-4">
            <Link to="/" aria-label="Cannect — página inicial">
              <img src={logo} alt="Cannect" className="h-[18px] w-[108px] object-contain" />
            </Link>

            <SearchAutocomplete
              isLogged={isLogged}
              className="w-[199px] shrink-0"
              inputClassName={SEARCH_INPUT_CLASSES}
            />

            <nav aria-label="Navegação principal">
              <ul className="m-0 flex list-none items-center gap-2 p-0">
                {menuConfig.map((item) => (
                  <li key={item.title}>
                    <HeaderPill
                      variant="nav"
                      isActive={isActive(item.path)}
                      title={item.title}
                      path={item.path}
                      external={item.external}
                      onClick={item.onClick}
                    />
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}

        <div className="flex shrink-0 items-center gap-3">
          {!isPublicFlow && (
            <>
              <HeaderPill
                variant="outline"
                isActive={isActive(headerCtas.professionals.path)}
                title={headerCtas.professionals.title}
                path={headerCtas.professionals.path}
                external={headerCtas.professionals.external}
              />
              <HeaderPill variant="solid" title={headerCtas.appointment.title} path={headerCtas.appointment.path} />
            </>
          )}

          <div className="flex items-center gap-3">
            {!isPublicFlow && !isAssistantFlow && !isDigitalJourneyFlow && (
              <>
                <Link to="/meus-favoritos" className={ICON_BUTTON_CLASSES}>
                  <LuHeart className="h-4 w-4" />
                  <span className="sr-only">Favoritos</span>
                </Link>

                {isLogged ? (
                  <Link to="/carrinho" className={ICON_BUTTON_CLASSES}>
                    {cartIconContent}
                  </Link>
                ) : (
                  <button type="button" onClick={() => setIsCartSheetOpen(true)} className={ICON_BUTTON_CLASSES}>
                    {cartIconContent}
                  </button>
                )}
              </>
            )}

            {!isPublicFlow && isAssistantFlow && !isDigitalJourneyFlow && (
              <button type="button" onClick={handleAssistantContinue} className={ICON_BUTTON_CLASSES}>
                <LuFileSpreadsheet className="h-4 w-4" />
                <CounterBadge quantity={cartProductsQuantity} isLoading={isLoadingCartQuantity} />
                <span className="sr-only">Prescrição</span>
              </button>
            )}

            {!isPublicFlow && isDigitalJourneyFlow && !isAssistantFlow && <DigitalJourney.SheetJourneySummary />}

            <Navigation.Menu>
              <AccountMenuItem
                className="flex"
                signedUserName={signedUserName}
                handleLogout={handleLogout}
                isLogged={isLogged}
                showSubscriptions={showSubscriptions}
              />
            </Navigation.Menu>
          </div>
        </div>
      </div>

      <CartSheet isOpen={isCartSheetOpen} onClose={() => setIsCartSheetOpen(false)} />
    </>
  )
}
